<template>
  <label class="kds-input" :class="`kds-input--${type}`">
    <span v-if="label" class="kds-input__label">{{ label }}</span>
    <span
      class="kds-input__field"
      :class="{
        'is-error': state === 'error' || !!error,
        'is-disabled': state === 'disabled' || disabled
      }"
    >
      <KdsIcon
        v-if="resolvedPrefixIcon"
        class="kds-input__icon kds-input__icon--prefix"
        :name="resolvedPrefixIcon"
        :size="16"
      />

      <textarea
        v-if="type === 'textarea'"
        :id="id"
        :name="name"
        class="kds-input__control kds-input__control--textarea"
        :class="{ 'kds-input__control--autogrow': autogrow }"
        :value="modelValue"
        :placeholder="placeholder"
        :disabled="state === 'disabled' || disabled"
        :aria-invalid="state === 'error' || !!error ? 'true' : undefined"
        :aria-describedby="error || helper ? hintId : undefined"
        :maxlength="maxlength"
        @input="onInput"
      />
      <select
        v-else-if="type === 'select'"
        :id="id"
        :name="name"
        class="kds-input__control kds-input__control--select"
        :value="modelValue"
        :disabled="state === 'disabled' || disabled"
        :aria-invalid="state === 'error' || !!error ? 'true' : undefined"
        :aria-describedby="error || helper ? hintId : undefined"
        @change="onChange"
      >
        <slot name="options" />
      </select>
      <input
        v-else
        :id="id"
        :name="name"
        class="kds-input__control"
        :type="resolvedInputType"
        :value="modelValue"
        :placeholder="placeholder"
        :disabled="state === 'disabled' || disabled"
        :aria-invalid="state === 'error' || !!error ? 'true' : undefined"
        :aria-describedby="error || helper ? hintId : undefined"
        :autocomplete="autocomplete"
        :inputmode="inputmode"
        :maxlength="maxlength"
        @input="onInput"
      />

      <button
        v-if="clearable && modelValue"
        type="button"
        class="kds-input__clear"
        tabindex="-1"
        aria-label="Clear"
        @click="onClear"
      >
        <KdsIcon name="x" :size="14" />
      </button>

      <span v-else-if="$slots.suffix" class="kds-input__suffix">
        <slot name="suffix" />
      </span>

      <KdsIcon
        v-else-if="resolvedSuffixIcon"
        class="kds-input__icon kds-input__icon--suffix"
        :name="resolvedSuffixIcon"
        :size="16"
      />
    </span>
    <span
      v-if="error || helper"
      :id="hintId"
      class="kds-input__hint"
      :class="{ 'is-error': !!error }"
    >
      {{ error || helper }}
    </span>
  </label>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import KdsIcon from './KdsIcon.vue';
import type {
  KdsIconName,
  KdsInputState,
  KdsInputType
} from '@/kds/types';

interface Props {
  modelValue?: string | number;
  type?: KdsInputType;
  state?: KdsInputState;
  label?: string;
  placeholder?: string;
  helper?: string;
  error?: string;
  disabled?: boolean;
  prefixIcon?: KdsIconName;
  suffixIcon?: KdsIconName;
  /** D2 — maxlength propagated to <input> and <textarea> */
  maxlength?: number;
  /** M1 — additional HTML attribute forwarding */
  name?: string;
  id?: string;
  autocomplete?: string;
  inputmode?: string;
  /** M1 — debounce in ms */
  debounce?: number;
  /** M1 — show clear button */
  clearable?: boolean;
  /** M1 — textarea autogrow */
  autogrow?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: '',
  type: 'text',
  state: 'default',
  label: undefined,
  placeholder: undefined,
  helper: undefined,
  error: undefined,
  disabled: false,
  prefixIcon: undefined,
  suffixIcon: undefined,
  maxlength: undefined,
  name: undefined,
  id: undefined,
  autocomplete: undefined,
  inputmode: undefined,
  debounce: undefined,
  clearable: false,
  autogrow: false
});

const emit = defineEmits<{
  'update:modelValue': [value: string];
}>();

// Stable id for aria-describedby linkage
const uid = Math.random().toString(36).slice(2, 8);
const hintId = computed(() => `kds-input-hint-${props.id ?? uid}`);

// Auto-pick contextual icons when the type implies one.
const resolvedPrefixIcon = computed<KdsIconName | undefined>(() => {
  if (props.prefixIcon) return props.prefixIcon;
  if (props.type === 'search') return 'search';
  return undefined;
});

const resolvedSuffixIcon = computed<KdsIconName | undefined>(() => {
  if (props.suffixIcon) return props.suffixIcon;
  if (props.type === 'select') return 'chevron-down';
  return undefined;
});

const resolvedInputType = computed<string>(() => {
  if (props.type === 'search') return 'search';
  if (props.type === 'password') return 'password';
  return 'text';
});

// Debounce support
let debounceTimer: ReturnType<typeof setTimeout> | null = null;

function onInput(event: Event) {
  const target = event.target as HTMLInputElement | HTMLTextAreaElement;
  const value = target.value;
  if (props.debounce) {
    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => {
      emit('update:modelValue', value);
    }, props.debounce);
  } else {
    emit('update:modelValue', value);
  }
}

function onChange(event: Event) {
  const target = event.target as HTMLSelectElement;
  emit('update:modelValue', target.value);
}

function onClear() {
  emit('update:modelValue', '');
}
</script>

<style scoped lang="scss">
/*
 * KdsInput uses semantic tokens defined in src/css/kds-tokens.scss:
 *   --kds-input-border, --kds-input-border-hover, --kds-input-border-focus
 *   --kds-input-label, --kds-input-value, --kds-input-placeholder
 *   --kds-input-option-bg-hover, --kds-input-option-bg-selected, --kds-input-option-text
 *   --kds-input-disabled-bg
 */
.kds-input {
  display: inline-flex;
  flex-direction: column;
  gap: var(--space-2);
  width: 100%;

  &__label {
    font-family: var(--font-ui);
    font-size: var(--fs-body-sm);
    color: var(--kds-input-label);
  }

  &__field {
    display: inline-flex;
    align-items: center;
    gap: var(--space-2);
    border: var(--bw) solid var(--kds-input-border);
    border-radius: var(--r-3);
    background: var(--paper);
    transition:
      border-color var(--d-fast) var(--ease-out),
      box-shadow var(--d-fast) var(--ease-out);

    &:not(.is-disabled):not(.is-error):not(:focus-within):hover {
      border-color: var(--kds-input-border-hover);
    }

    &:focus-within {
      border-color: var(--ink);
      box-shadow: var(--shadow-focus);
    }

    &.is-error {
      border-color: var(--danger);
    }

    &.is-disabled {
      background: var(--kds-input-disabled-bg);
      color: var(--stone-2);
    }
  }

  &__icon {
    color: var(--stone);

    &--prefix {
      margin-left: 12px;
    }

    &--suffix {
      margin-right: 12px;
    }
  }

  &__clear {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: none;
    background: transparent;
    color: var(--stone);
    cursor: pointer;
    padding: 0;
    line-height: 1;

    &:hover {
      color: var(--ink);
    }
  }

  &__suffix {
    display: inline-flex;
    align-items: center;
    margin-right: 12px;
    flex-shrink: 0;
  }

  &__control {
    flex: 1;
    min-width: 0;
    padding: 10px 12px;
    border: none;
    background: transparent;
    color: var(--ink);
    caret-color: var(--ink);
    font-family: var(--font-ui);
    font-size: var(--fs-body);
    line-height: var(--lh-body);
    outline: none;

    // The global `:focus-visible` rule in app.scss applies a 3px shadow ring
    // to every focusable element. The focus ring is already drawn on the
    // wrapping `__field` (`:focus-within` above), so suppress it on the inner
    // control to avoid a second, inset ring around the <input>.
    &:focus,
    &:focus-visible {
      box-shadow: none;
    }

    &::placeholder {
      color: var(--stone-2);
    }

    &--textarea {
      min-height: 96px;
      resize: vertical;
      border: 0;
      outline: none;
      box-shadow: none;
      appearance: none;
    }

    &--autogrow {
      resize: none;
      overflow: hidden;
    }

    &--select {
      appearance: none;
      -webkit-appearance: none;
      padding-right: var(--space-5);
    }
  }

  &__icon--prefix + &__control {
    padding-left: 0;
  }

  &__control:has(+ &__icon--suffix),
  &__control:has(+ &__clear),
  &__control:has(+ &__suffix) {
    padding-right: 0;
  }

  &__hint {
    font-family: var(--font-ui);
    font-size: var(--fs-body-sm);
    color: var(--stone);

    &.is-error {
      color: var(--danger);
    }
  }
}

// Neutralise Safari / Chrome / Firefox native search field chrome.
// `appearance: textfield` preserves OS text-input styling (no spinner, no
// native search chrome) while allowing the KDS border/shadow to take over,
// unlike `appearance: none` which may strip focus outlines in some browsers.
input[type='search'].kds-input__control {
  appearance: textfield;
  -webkit-appearance: textfield;

  &::-webkit-search-cancel-button {
    display: none;
  }

  &::-webkit-search-decoration {
    display: none;
  }
}
</style>
